// MockPortal — v0.8.0 (Dev / Mock Stripe)
//
// Fake Stripe customer portal used during development when STRIPE_MOCK=true.
// Lets you cancel the mock subscription (resets to trial_expired state) or
// reactivate it (sets back to active Pro). Return button goes back to /account.
//
// Only reachable via the mock-mode portal redirect in development.

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { API_BASE_URL } from '../services/api';

async function callReset(state) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('Not signed in.');

  const res = await fetch(`${API_BASE_URL}/mock-stripe-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ state }),
  });

  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Action failed.');
  return data;
}

export default function MockPortal() {
  const [params]  = useSearchParams();
  const returnUrl = params.get('return') || '/account';
  const [loading, setLoading] = useState(null); // tracks which action is running
  const [message, setMessage] = useState(null);
  const [error, setError]     = useState(null);

  async function handleAction(state, label) {
    setError(null);
    setMessage(null);
    setLoading(state);
    try {
      await callReset(state);
      setMessage(`✓ ${label} — returning to account…`);
      setTimeout(() => { window.location.href = returnUrl; }, 1200);
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>

        <div style={mockBadgeStyle}>🧪 Mock Billing Portal — Dev Mode Only</div>

        <div style={logoStyle}>Rentava</div>
        <h2 style={headingStyle}>Manage your subscription</h2>
        <p style={subStyle}>
          Simulate subscription state changes to test the paywall UI.
        </p>

        {message && <div style={successStyle}>{message}</div>}
        {error   && <div style={errorStyle}>{error}</div>}

        <div style={actionsStyle}>
          <ActionButton
            label="Reactivate / Renew Pro"
            desc="Sets status to active, 30 days remaining"
            color="#2563eb"
            loading={loading === 'active'}
            onClick={() => handleAction('active', 'Pro reactivated')}
          />
          <ActionButton
            label="Cancel Subscription"
            desc="Ends access — simulates a canceled plan"
            color="#ef4444"
            loading={loading === 'canceled'}
            onClick={() => handleAction('canceled', 'Subscription canceled')}
          />
          <ActionButton
            label="Reset to Fresh Trial (7 days)"
            desc="Useful for re-testing the full onboarding flow"
            color="#6b7280"
            loading={loading === 'trialing'}
            onClick={() => handleAction('trialing', 'Reset to trial')}
          />
        </div>

        <button style={returnButtonStyle} onClick={() => window.location.href = returnUrl}>
          ← Return to Account
        </button>

        <p style={noteStyle}>
          These actions write directly to Supabase — no Stripe calls are made.
          Available in dev mode only (STRIPE_MOCK=true).
        </p>
      </div>
    </div>
  );
}

function ActionButton({ label, desc, color, loading, onClick }) {
  return (
    <button
      style={{
        display: 'block',
        width: '100%',
        background: loading ? '#f3f4f6' : '#ffffff',
        border: `1px solid ${loading ? '#e5e7eb' : color}`,
        borderRadius: '10px',
        padding: '14px 16px',
        textAlign: 'left',
        cursor: loading ? 'not-allowed' : 'pointer',
        marginBottom: '10px',
      }}
      onClick={onClick}
      disabled={!!loading}
    >
      <div style={{ fontSize: '14px', fontWeight: 700, color: loading ? '#9ca3af' : color }}>
        {loading ? 'Working…' : label}
      </div>
      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{desc}</div>
    </button>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyle = {
  minHeight: '100vh',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '36px 32px',
  maxWidth: '440px',
  width: '100%',
  boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
};

const mockBadgeStyle = {
  background: '#fef3c7',
  color: '#92400e',
  borderRadius: '8px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '20px',
};

const logoStyle = {
  fontSize: '16px',
  fontWeight: 800,
  color: '#111827',
  marginBottom: '16px',
};

const headingStyle = {
  fontSize: '22px',
  fontWeight: 700,
  color: '#111827',
  margin: '0 0 8px',
};

const subStyle = {
  fontSize: '14px',
  color: '#6b7280',
  marginBottom: '24px',
};

const actionsStyle = {
  marginBottom: '20px',
};

const returnButtonStyle = {
  display: 'block',
  width: '100%',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: '10px',
  padding: '12px',
  fontSize: '14px',
  color: '#6b7280',
  cursor: 'pointer',
  marginBottom: '20px',
};

const successStyle = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#15803d',
  borderRadius: '8px',
  padding: '10px 12px',
  fontSize: '13px',
  marginBottom: '16px',
  textAlign: 'center',
};

const errorStyle = {
  color: '#ef4444',
  fontSize: '13px',
  marginBottom: '12px',
  textAlign: 'center',
};

const noteStyle = {
  fontSize: '11px',
  color: '#9ca3af',
  textAlign: 'center',
  lineHeight: 1.5,
  margin: 0,
};
